import Header from "../components/Header";
import {AlertCircle, Delete, Download, Edit2, Edit3, ExternalLink, Plus, Trash, Trash2} from "react-feather";
import {NavLink, useParams} from "react-router-dom";
import InputWrapper from "../../components/InputWrapper";
import Form from "../components/Form";
import {useContext, useEffect, useState} from "react";
import Data from "../../Data";
import ukMoment from "../../../../utils/ukTime";
import Meta from "../../../components/Meta";
import AppContext from "../../../../context";
import {formatDuration} from "../../utils/duration";

const Booking = () => {
    const { id } = useParams();
    const context = useContext(AppContext);
    const isHybrid = context.domain?.businessType === 'hybrid' || context.domain?.businessType === 'hybrid-new';
    const formatBookingAmount = (amount) => isHybrid ? `${Number(amount || 0)} token${Number(amount || 0) === 1 ? '' : 's'}` : `£${amount}`;

    const [booking, setBooking] = useState( null);
    const [loading, setLoading] = useState(true);
    const [cancelError, setCancelError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [recheckResult, setRecheckResult] = useState(null);

    useEffect(async () => {
        try {
            const booking = await Data.fetchExperienceBooking({ id })
            setBooking(booking.data)
        } catch(error){
            window.location.href = '/admin/class-bookings'
        } finally {
            setLoading(false)
        }
    }, []);

    if(!booking) {
        return (
            <>
                <Meta title={loading ? "Loading Booking" : "Booking Not Found"} />
                <Header heading={loading ? "Loading Booking" : "Booking Not Found"} backToLabel={"Class Bookings"} backToTo={"/admin/class-bookings"} />
                <div className={"p-3 lg:p-6"}>
                    <div className={"bg-white w-full rounded-lg border drop-shadow-sm py-4 px-6"}>
                        <p className={"text-textGrey"}>{loading ? "Loading..." : "This booking could not be found for this business."}</p>
                    </div>
                </div>
            </>
        );
    }

    const user = booking.User || {};
    const event = booking.Event || {};
    const experience = event.Experience || {};
    const isImportedBooking = String(booking.paymentId || '').startsWith('import_');

    const recheckPaymentStatus = async () => {
        setRefreshing(true)
        setRecheckResult(null)
        try {
            const response = await Data.recheckExperienceBookingPayment(id)
            setRecheckResult({ success: response.data?.status !== 'not_paid', message: response.data?.message })
            const updated = await Data.fetchExperienceBooking({ id })
            setBooking(updated.data)
        } catch(error) {
            setRecheckResult({ success: false, message: error?.response?.data?.message || "Could not verify payment status. Please try again or contact support." })
        } finally {
            setRefreshing(false)
        }
    }

    const cancel = async (refund) => {
        setLoading(true)
        try {
            if(window.confirm('Are you sure you want to cancel this booking?')) {
                await Data.cancelExperienceBooking({ id, refund })
                window.location.reload()
            }
        } catch(error){
            setCancelError(error?.response?.data?.message || "There was an error cancelling the booking.");
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            <Meta title={`Ref: ${booking.reference}`} />
            <Header heading={booking.reference} backToLabel={"Class Bookings"} backToTo={"/admin/class-bookings"} />
            <div className={"p-3 lg:p-6 grid grid-cols-1 gap-3 lg:gap-6 w-full overflow-x-scroll"}>

                {booking.status === "Cancelled" ?
                    <div className={"bg-red-200 text-white w-full rounded-lg drop-shadow-sm [&>*]:py-4 [&>*]:px-6"}>
                        <div className={"flex items-center gap-2 flex-wrap"}>
                            <AlertCircle size={20} className={"text-red-900"}/>
                            <p className={"text-red-900"}>This booking was cancelled by {booking.cancelledBy} on {ukMoment(booking.cancelledOn).format("Do MMMM")} at {ukMoment(booking.cancelledOn).format("HH:mm")}.{booking.refundId ? ` ‘Payment Refund Reference: ${booking.refundId}` : null}</p>
                        </div>
                    </div> : null}

                {booking.paymentVerificationStatus === 'mismatch' ?
                    <div className={"bg-red-200 text-white w-full rounded-lg drop-shadow-sm [&>*]:py-4 [&>*]:px-6"}>
                        <div className={"flex items-center gap-2 flex-wrap"}>
                            <AlertCircle size={20} className={"text-red-900"}/>
                            <p className={"text-red-900"}>
                                This booking is marked as paid, but our automatic check against the payment provider{booking.paymentVerifiedAt ? ` on ${ukMoment(booking.paymentVerifiedAt).format("Do MMMM")} at ${ukMoment(booking.paymentVerifiedAt).format("HH:mm")}` : ''} found no matching successful payment.
                                {booking.paymentVerificationReason ? ` ${booking.paymentVerificationReason}` : ''} Please confirm with the customer before treating this booking as paid.
                            </p>
                        </div>
                    </div> : null}

                <div className={"bg-white w-full rounded-lg border drop-shadow-sm [&>*]:py-4 [&>*]:px-6"}>
                    <div className={"my-2 flex justify-between items-center"}>
                        <p className={"text-lg"}>Booking Details</p>
                    </div>

                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Status</p>
                        <p className={"text-textGrey col-span-2"}>{booking.status}</p>
                    </div>
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Bundle</p>
                        <p className={"text-textGrey col-span-2"}>{booking.BundlePurchase ? (booking.BundlePurchase?.Bundle?.name || 'Yes') : 'No'}</p>
                    </div>
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Reference</p>
                        <p className={"text-textGrey col-span-2"}>{booking.reference}</p>
                    </div>
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Tickets</p>
                        <p className={"text-textGrey col-span-2"}>{booking.tickets}</p>
                    </div>
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Name</p>
                        <p className={"text-textGrey col-span-2"}>{user.firstName} {user.lastName}</p>
                    </div>
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Email Address</p>
                        <p className={"text-textGrey col-span-2"}>{user.email}</p>
                    </div>
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Phone Number</p>
                        <p className={"text-textGrey col-span-2"}>{user.phoneNumber}</p>
                    </div>
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Booked On</p>
                        <p className={"text-textGrey col-span-2"}>{ukMoment(booking.bookedOn).format("MMM Do YYYY, H:mm A")}</p>
                    </div>
                    {booking.notes ? (
                        <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                            <p>Notes</p>
                            <p className={"text-textGrey col-span-2 whitespace-pre-wrap"}>{booking.notes}</p>
                        </div>
                    ) : null}
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Cancelled On</p>
                        <p className={"text-textGrey col-span-2"}>{booking.cancelledOn ? ukMoment(booking.cancelledOn).format("MMM Do YYYY, H:mm A") : '-'}</p>
                    </div>
                </div>

                <div className={"bg-white w-full rounded-lg border drop-shadow-sm [&>*]:py-4 [&>*]:px-6"}>
                    <div className={"my-2 flex justify-between items-center"}>
                        <p className={"text-lg"}>Event Details</p>
                    </div>

                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Experience</p>
                        <NavLink to={`/admin/experiences/${experience._id}`}
                                 className={"text-textGrey col-span-2 underline flex items-center gap-1"}>{experience.name}
                            <ExternalLink size={16}/></NavLink>
                    </div>
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Date &amp; Time</p>
                        <NavLink to={`/admin/upcoming-events/${event._id}`}
                                 className={"text-textGrey col-span-2 underline flex items-center gap-1"}>{ukMoment(event.date).format("MMM Do YYYY, H:mm A")}
                            <ExternalLink size={16}/></NavLink>
                    </div>
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Duration</p>
                        <p className={"text-textGrey col-span-2"}>{formatDuration(experience.duration)}</p>
                    </div>
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Location</p>
                        <p className={"text-textGrey col-span-2"}>{experience.location}</p>
                    </div>
                    {booking.access?.pin ? (
                        <>
                            <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                                <p>Access PIN</p>
                                <p className={"text-textGrey col-span-2"}>{booking.access.pin}</p>
                            </div>
                            <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                                <p>Access Window</p>
                                <p className={"text-textGrey col-span-2"}>{ukMoment(booking.access.start).format("MMM Do YYYY, HH:mm")} - {ukMoment(booking.access.end).format("HH:mm")}</p>
                            </div>
                            <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                                <p>RemoteLock Status</p>
                                <p className={"text-textGrey col-span-2"}>{booking.access.remoteLock?.status || '-'}</p>
                            </div>
                        </>
                    ) : null}
                </div>

                <div className={"bg-white w-full rounded-lg border drop-shadow-sm [&>*]:py-4 [&>*]:px-6"}>
                    <div className={"my-2 flex justify-between items-center"}>
                        <p className={"text-lg"}>Payment Information</p>
                    </div>

                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Ticket Price</p>
                        <p className={"text-textGrey col-span-2"}>{formatBookingAmount(booking.ticketPrice)} each</p>
                    </div>
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>{isHybrid ? 'Tokens Used' : 'Total Amount'}</p>
                        <p className={"text-textGrey col-span-2"}>{formatBookingAmount(booking.totalPrice)}</p>
                    </div>
                    {!isHybrid && Number(booking.depositAmount) > 0 ? (
                        <>
                            <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                                <p>Deposit Paid</p>
                                <p className={"text-textGrey col-span-2"}>{formatBookingAmount(booking.depositAmount)}</p>
                            </div>
                            <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                                <p>Balance Remaining</p>
                                <p className={"text-textGrey col-span-2"}>{formatBookingAmount(Math.max(0, Number(booking.totalPrice) - Number(booking.depositAmount)))}</p>
                            </div>
                        </>
                    ) : null}
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Status</p>
                        <p className={"text-textGrey col-span-2 capitalize"}>{booking.status}</p>
                    </div>
                    {isImportedBooking ? (
                        <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                            <p>Imported</p>
                            <p className={"text-textGrey col-span-2"}>Yes</p>
                        </div>
                    ) : null}
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Payment Reference</p>
                        <p className={"text-textGrey col-span-2"}>{booking.paymentId}</p>
                    </div>
                </div>

                {booking.paymentMethod === 'processor' && !isImportedBooking ?
                    <div className={"bg-white w-full rounded-lg border drop-shadow-sm [&>*]:py-4 [&>*]:px-6"}>
                        <div className={"my-2"}>
                            <p className={"text-lg"}>Recheck Payment Status</p>
                            <p className={"text-textGrey"}>Checks this booking's payment directly with the payment provider and updates the booking to match - including reverting it to Payment Failed if it's currently marked paid but the provider has no matching payment.</p>
                        </div>
                        <div className={"mt-2 border-t pt-3"}>
                            {recheckResult ? (
                                <p className={`text-sm mb-2 ${recheckResult.success ? 'text-green-700' : 'text-red-600'}`}>{recheckResult.message}</p>
                            ) : null}
                            <button
                                onClick={recheckPaymentStatus}
                                disabled={refreshing}
                                className={"border-0 bg-yellow-200 text-yellow-900 px-2.5 py-1.5 rounded-md hover:opacity-80 transition disabled:opacity-50"}>
                                {refreshing ? 'Checking...' : 'Recheck Payment Status'}
                            </button>
                        </div>
                    </div>
                : null}

                {booking.status === "Paid" && !isImportedBooking ?
                    <div className={"bg-white w-full rounded-lg border drop-shadow-sm [&>*]:py-4 [&>*]:px-6"}>
                        <div className={"my-2"}>
                            <p className={"text-lg"}>Cancel Booking</p>
                            {booking.BundlePurchase ? (
                                <p className={"text-textGrey"}>This booking was made as part of a bundle and cannot be individually cancelled.</p>
                            ) : (
                                <p className={"text-textGrey"}>Cancelling this booking will trigger an automatic email to the email address listed above.</p>
                            )}
                        </div>




                    {cancelError ? (
                        <div className={"text-red-600 text-sm mt-2"}>{cancelError}</div>
                    ) : null}

                            {!booking.BundlePurchase ? (
                                <div className={"mt-2 flex gap-2 border-t"}>
                                    <button
                                        onClick={cancel.bind(this, false)}
                                        className={"border-0 bg-red-200 text-red-900 px-2.5 py-1.5 rounded-md hover:opacity-80 transition"}>Cancel
                                    </button>
                                    <button
                                        onClick={cancel.bind(this, true)}
                                        className={"border-0 bg-red-200 text-red-900 px-2.5 py-1.5 rounded-md hover:opacity-80 transition"}>Cancel
                                        + Full Refund
                                    </button>
                                </div>
                            ) : null}
                    </div>
                : null}

            </div>
        </>
    )
}

export default Booking
